import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Link,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UIKit/Button';
import TextField from '../components/UIKit/TextField';
import { useTranslation } from 'react-i18next';

const Register = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});
  const [registerError, setRegisterError] = useState('');

  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName) {
      newErrors.fullName = t('fullName_required');
    } else if (formData.fullName.length < 3) {
      newErrors.fullName = t('fullName_length');
    }
    if (!formData.email) {
      newErrors.email = t('email_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('invalid_email');
    }
    if (!formData.password) {
      newErrors.password = t('password_required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('password_length');
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('confirm_password_required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwords_not_match');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Очищаем ошибку при вводе
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (registerError) {
      setRegisterError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await register(
        formData.fullName,
        formData.email,
        formData.password,
        formData.confirmPassword
      );
      setRegisterError('');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error.response?.data || error);
      const data = error.response?.data;
      if (data?.error === 'Validation Error' && Array.isArray(data.errors) && data.errors.length) {
        const fieldErrors = {};
        data.errors.forEach((e) => {
          if (e?.path && e?.message) {
            fieldErrors[e.path] = e.message;
          }
        });
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        setRegisterError('');
      } else {
        const apiMessage = data?.message || data?.error;
        setRegisterError(apiMessage || t('register_error'));
      }
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" gutterBottom>
            {t('register_title')}
          </Typography>

          {/* Google OAuth Button — primary action */}
          <Button
            type="button"
            fullWidth
            variant="contained"
            onClick={loginWithGoogle}
            size="large"
            sx={{
              mt: 2,
              mb: 1,
              py: 1.5,
              fontSize: '1rem',
            }}
            startIcon={
              <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <g fill="none" fillRule="evenodd">
                  <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.33-1.553-5.036-3.64H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.423 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.67 5.203 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </g>
              </svg>
            }
          >
            Sign up with Google
          </Button>

          <Divider sx={{ width: '100%', my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('or_email_register')}
            </Typography>
          </Divider>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: '100%' }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="fullName"
              label={t('fullName_label')}
              name="fullName"
              autoComplete="name"
              autoFocus
              value={formData.fullName}
              onChange={handleChange}
              error={!!errors.fullName}
              helperText={errors.fullName}
              placeholder={t('fullName_placeholder')}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('email_label')}
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              placeholder={t('email_placeholder')}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('password_label')}
              type={showPassword.password ? 'text' : 'password'}
              id="password"
              autoComplete="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              placeholder={t('password_placeholder')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('password')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('confirm_password_label')}
              type={showPassword.confirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              placeholder={t('confirm_password_placeholder')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('confirmPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {registerError && (
              <Typography variant="body2" color="error" align="center" sx={{ mt: 1, mb: 1 }}>
                {registerError}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="outlined"
              sx={(theme) => ({
                mt: registerError ? 2 : 3,
                mb: 2,
                backgroundColor: theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : theme.palette.common.white,
                color: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.divider,
                }
              })}
            >
              {t('register_button')}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/login')}
              >
                {t('have_account')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;

